import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import "../../App.css";
import Menu from '../../panel/Menu';
import moment from 'moment';

function CheckinReport() {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });

  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    if (!selectedDate) return;

    setLoading(true);

    const q = query(
      collection(db, 'checkindetails'),
      where('checkindate', '>=', selectedDate)
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        try {
          const detailDocs = querySnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data()
          }));

          // Group room records by booking ID
          const groupedByBooking = {};
          detailDocs.forEach((item) => {
            const key = item.bookingid || item.id;
            if (!groupedByBooking[key]) {
              groupedByBooking[key] = [];
            }
            groupedByBooking[key].push(item);
          });

          // Fetch unique bookings from Firestore
          const uniqueBookingIds = Object.keys(groupedByBooking).filter(
            (bId) => bId && !bId.startsWith('dummy')
          );

          const bookingDataMap = {};
          await Promise.all(
            uniqueBookingIds.map(async (bId) => {
              try {
                const bSnap = await getDoc(doc(db, 'bookings', bId));
                if (bSnap.exists()) {
                  bookingDataMap[bId] = bSnap.data();
                }
              } catch (err) {
                console.error("Error fetching booking for id", bId, err);
              }
            })
          );

          // Build merged row list: One row per booking with all rooms displayed
          const combinedList = Object.keys(groupedByBooking).map((bookingId) => {
            const items = groupedByBooking[bookingId];
            const firstItem = items[0];
            const parentBooking = bookingDataMap[bookingId] || {};

            // Extract all room numbers for this booking and format them side-by-side
            const roomsArray = items
              .map((d) => d.roomname || d.roomno)
              .filter(Boolean);
            const roomDisplay = [...new Set(roomsArray)].join(', ') || 'N/A';

            const guestName =
              parentBooking.customers?.custname ||
              parentBooking.custname ||
              firstItem.customers?.custname ||
              'N/A';

            const foodPlan = firstItem.planname || firstItem.planid || 'N/A';

            // Extract un-divided adults and children counts
            const adults = Number(
              parentBooking.adults ??
              parentBooking.pax ??
              parentBooking.guestsCount ??
              1
            );
            const children = Number(
              parentBooking.children ??
              parentBooking.childs ??
              parentBooking.kids ??
              0
            );

            const totalPax = adults + children;

            return {
              id: bookingId,
              rooms: roomDisplay,
              roomCount: roomsArray.length,
              guestName: guestName,
              foodPlan: foodPlan,
              arrivalDate: firstItem.checkindate,
              departureDate: firstItem.checkoutdate,
              adults: isNaN(adults) ? 1 : adults,
              children: isNaN(children) ? 0 : children,
              totalPax: isNaN(totalPax) ? 1 : totalPax
            };
          });

          setRows(combinedList);
          setCurrentPage(1);
        } catch (error) {
          Toast.fire({ icon: 'error', title: error.message });
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        setLoading(false);
        Toast.fire({ icon: 'error', title: error.message });
      }
    );

    return () => unsubscribe();
  }, [selectedDate]);

  const formatDateSafe = (dateVal, format = 'DD/MM/YYYY') => {
    if (!dateVal) return '-';
    if (typeof dateVal.toDate === 'function') {
      return moment(dateVal.toDate()).format(format);
    }
    return moment(dateVal).format(format);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((item) => {
      // 1. Hide invalid records
      const isValidRoom = item.rooms && item.rooms !== 'N/A';
      const isValidGuest = item.guestName && item.guestName !== 'N/A';
      if (!isValidRoom || !isValidGuest) return false;

      // 2. Only show CP and CP OTA plans
      const plan = (item.foodPlan || '').toUpperCase().trim();
      const isCP = plan === 'CP' || plan === 'CP OTA';
      if (!isCP) return false;

      // 3. Search query filter
      if (!searchText) return true;
      const search = searchText.toLowerCase();
      return (
        item.rooms.toLowerCase().includes(search) ||
        item.guestName.toLowerCase().includes(search) ||
        item.foodPlan.toLowerCase().includes(search)
      );
    });
  }, [rows, searchText]);

  // Aggregate Metrics
  const totalRoomUnits = useMemo(() => {
    return filteredRows.reduce((acc, curr) => acc + curr.roomCount, 0);
  }, [filteredRows]);

  const totalAdultsCount = useMemo(() => {
    return filteredRows.reduce((acc, curr) => acc + curr.adults, 0);
  }, [filteredRows]);

  const totalChildrenCount = useMemo(() => {
    return filteredRows.reduce((acc, curr) => acc + curr.children, 0);
  }, [filteredRows]);

  const grandTotalPax = useMemo(() => {
    return totalAdultsCount + totalChildrenCount;
  }, [totalAdultsCount, totalChildrenCount]);

  // Pagination Calculations
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handlePrint = () => window.print();

  return (
    <div className="bg-light min-vh-100">
      {/* Hide menu during printing */}
      <div className="d-print-none">
        <Menu />
      </div>

      <div className="content-wrapper p-3 p-md-4" style={{ minHeight: '100vh' }}>
        
        {/* ================= PRINT-ONLY HEADER ================= */}
        <div className="d-none d-print-block w-100 mb-4">
          <div className="text-center pb-3 mb-3" style={{ borderBottom: '2px solid #2d3748' }}>
            <h2 className="font-weight-bold mb-1" style={{ color: '#1a202c' }}>Dasara Executive</h2>
            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
              Chatrapati Shivaji Maharaj Chouk, Tuljapur, Dist - Dharashiv, MH - 413601
            </p>
            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
              Phone: +91 9975294940 | Email: dasaraexecutive@gmail.com
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="font-weight-bold m-0 text-uppercase" style={{ fontSize: '14px' }}>
              Check-in List (CP Plan)
            </h5>
            <span style={{ fontSize: '13px' }}>
              <strong>Date:</strong> {formatDateSafe(selectedDate, 'DD/MM/YYYY')}
            </span>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid p-0">
            
            {/* SCREEN PAGE HEADER */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 d-print-none">
              <div>
                <h3 className="font-weight-bold mb-1 text-dark" style={{ letterSpacing: '-0.5px' }}>
                  Check-in Report
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  Daily overview of room assignments, guest details, and meal plans.
                </p>
              </div>
              <div className="d-flex align-items-center mt-3 mt-md-0">
                <button
                  onClick={handlePrint}
                  className="btn btn-dark shadow-sm d-flex align-items-center"
                  style={{ borderRadius: '8px', padding: '8px 18px', fontWeight: '500' }}
                >
                  <i className="fa fa-print mr-2"></i> Print Report
                </button>
              </div>
            </div>

            {/* ANALYTICS SUMMARY CARDS (SCREEN ONLY) */}
            <div className="row mb-4 d-print-none">
              <div className="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Total Rooms
                    </span>
                    <h3 className="font-weight-bold text-dark mb-0 mt-1">{totalRoomUnits}</h3>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Adults
                    </span>
                    <h3 className="font-weight-bold text-primary mb-0 mt-1">{totalAdultsCount}</h3>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3 mb-2 mb-md-0">
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Children
                    </span>
                    <h3 className="font-weight-bold text-info mb-0 mt-1">{totalChildrenCount}</h3>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600' }}>
                      Total Pax
                    </span>
                    <h3 className="font-weight-bold text-success mb-0 mt-1">{grandTotalPax}</h3>
                  </div>
                </div>
              </div>
            </div>

            {/* CONTROLS & FILTER BAR */}
            <div className="card border-0 shadow-sm mb-4 d-print-none" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="row align-items-center">
                  <div className="col-12 col-md-4 mb-2 mb-md-0 d-flex align-items-center">
                    <label className="mr-2 mb-0 font-weight-bold text-secondary" style={{ fontSize: '13px', minWidth: '90px' }}>
                      Select Date:
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-8 d-flex justify-content-md-end">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search room no, guest name, or plan..."
                      style={{ maxWidth: '360px', borderRadius: '8px', fontSize: '13px' }}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* DATA TABLE */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
                    <p className="mt-2 text-muted" style={{ fontSize: '13px' }}>Loading records...</p>
                  </div>
                ) : (
                  <>
                    {/* SCREEN TABLE */}
                    <div className="table-responsive d-print-none">
                      <table className="table table-hover align-middle mb-0 text-nowrap">
                        <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #edf2f7' }}>
                          <tr className="text-secondary" style={{ fontSize: '12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                            <th className="py-3 px-3" style={{ width: '60px' }}>#</th>
                            <th className="py-3 px-3">Room No(s)</th>
                            <th className="py-3 px-3">Guest Name</th>
                            <th className="py-3 px-3">Food Plan</th>
                            <th className="py-3 px-3">Arrival</th>
                            <th className="py-3 px-3">Departure</th>
                            <th className="py-3 px-3 text-center">Adults</th>
                            <th className="py-3 px-3 text-center">Children</th>
                            <th className="py-3 px-3 text-center font-weight-bold">Total Pax</th>
                          </tr>
                        </thead>
                        <tbody style={{ fontSize: '13px' }}>
                          {currentRows.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-5 text-muted">
                                No check-in records found for {formatDateSafe(selectedDate, 'DD/MM/YYYY')}
                              </td>
                            </tr>
                          ) : (
                            currentRows.map((item, index) => (
                              <tr key={item.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                                <td className="py-3 px-3 text-muted">{indexOfFirstRow + index + 1}</td>
                                <td className="py-3 px-3 font-weight-bold text-dark">
                                  {item.rooms.split(', ').map((rm, i) => (
                                    <span
                                      key={i}
                                      className="badge badge-light border mr-1 px-2 py-1"
                                      style={{ fontSize: '12px' }}
                                    >
                                      {rm}
                                    </span>
                                  ))}
                                </td>
                                <td className="py-3 px-3 text-capitalize font-weight-bold text-dark">
                                  {item.guestName}
                                </td>
                                <td className="py-3 px-3">
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor: item.foodPlan === 'CP OTA' ? '#e9d8fd' : '#e2e8f0',
                                      color: item.foodPlan === 'CP OTA' ? '#6b46c1' : '#2d3748',
                                      fontSize: '11px',
                                      padding: '5px 8px'
                                    }}
                                  >
                                    {item.foodPlan}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-muted">{formatDateSafe(item.arrivalDate, 'DD/MM/YYYY')}</td>
                                <td className="py-3 px-3 text-muted">{formatDateSafe(item.departureDate, 'DD/MM/YYYY')}</td>
                                <td className="py-3 px-3 text-center">{item.adults}</td>
                                <td className="py-3 px-3 text-center">{item.children}</td>
                                <td className="py-3 px-3 text-center font-weight-bold text-dark">
                                  {item.totalPax}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* PRINT TABLE */}
                    <table className="table table-bordered table-sm text-nowrap d-none d-print-table w-100" style={{ fontSize: '11px' }}>
                      <thead className="thead-light">
                        <tr>
                          <th style={{ width: '5%', textAlign: 'center' }}>#</th>
                          <th style={{ width: '16%' }}>Room No(s)</th>
                          <th style={{ width: '31%' }}>Guest Name</th>
                          <th style={{ width: '12%' }}>Food Plan</th>
                          <th style={{ width: '11%' }}>Arrival</th>
                          <th style={{ width: '11%' }}>Departure</th>
                          <th style={{ width: '4%', textAlign: 'center' }}>Adult</th>
                          <th style={{ width: '4%', textAlign: 'center' }}>Child</th>
                          <th style={{ width: '6%', textAlign: 'center' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-2">No Records Found</td>
                          </tr>
                        ) : (
                          filteredRows.map((item, index) => (
                            <tr key={item.id}>
                              <td style={{ textAlign: 'center' }}>{index + 1}</td>
                              <td><b>{item.rooms}</b></td>
                              <td className="text-capitalize">{item.guestName}</td>
                              <td>{item.foodPlan}</td>
                              <td>{formatDateSafe(item.arrivalDate, 'DD-MM-YYYY')}</td>
                              <td>{formatDateSafe(item.departureDate, 'DD-MM-YYYY')}</td>
                              <td style={{ textAlign: 'center' }}>{item.adults}</td>
                              <td style={{ textAlign: 'center' }}>{item.children}</td>
                              <td style={{ textAlign: 'center' }}><b>{item.totalPax}</b></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>

              {/* ================= PRINT FOOTER ================= */}
              <div className="d-none d-print-block mt-3 pt-2" style={{ borderTop: '2px solid #2d3748', fontSize: '12px' }}>
                <div className="d-flex justify-content-between">
                  <span><strong>Total Allocated Rooms:</strong> {totalRoomUnits}</span>
                  <span>
                    <strong>Adults:</strong> {totalAdultsCount} | <strong>Children:</strong> {totalChildrenCount} | <strong>Total Pax:</strong> {grandTotalPax}
                  </span>
                </div>
              </div>

              {/* SCREEN PAGINATION */}
              {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 d-print-none" style={{ borderTop: '1px solid #edf2f7' }}>
                  <small className="text-muted" style={{ fontSize: '12px' }}>
                    Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredRows.length)} of {filteredRows.length} entries
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm m-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={handlePrev} style={{ borderRadius: '6px 0 0 6px' }}>Prev</button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={handleNext} style={{ borderRadius: '0 6px 6px 0' }}>Next</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}

            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CheckinReport;